/**
 * React-specific types for the mount adapter.
 *
 * React and ReactDOM are imported at runtime via the browser
 * import map rather than at compile time through `@types/react`,
 * so these interfaces describe the minimal surface the adapter
 * actually consumes - createElement + Component for JSX and
 * error boundaries, createRoot / hydrateRoot for mounting.
 * Importing real React types would force a compile-time
 * dependency on react's .d.ts files even though the adapter
 * never touches react itself during compilation.
 */

import type { FrameworkSettings } from '@sdx/core/mount';

export interface ReactLike {
  createElement: (...args: unknown[]) => unknown;
  Component: new (props: unknown) => {
    state: Record<string, unknown>;
    props: Record<string, unknown>;
    setState?: (...args: unknown[]) => void;
  };
  default?: ReactLike;
}

export interface ReactDomLike {
  createRoot: (container: Element) => ReactRoot;
  hydrateRoot: (
    container: Element,
    element: unknown,
    options?: {
      onRecoverableError?: (error: unknown, errorInfo: { componentStack?: string }) => void;
    },
  ) => ReactRoot;
  default?: ReactDomLike;
}

export interface ReactRoot {
  render: (element: unknown) => void;
  unmount: () => void;
}

export function readReactSettings(): FrameworkSettings {
  const settings = drupalSettings.sdx_react;
  return (settings && typeof settings === 'object' ? settings : {}) as FrameworkSettings;
}
